import { POOL } from '@/types/channels';

import type { JobPostingStep } from '../types';

export const JOB_POSTING_STEPS: readonly JobPostingStep[] = [
  'select-channels',
  'job-details',
  'pool-setup',
  'campaign-details',
  'summary',
  'result',
] as const;

export const INITIAL_JOB_POSTING_STEP: JobPostingStep = JOB_POSTING_STEPS[0];

export const JOB_POSTING_STEP_ORDER = Object.fromEntries(
  JOB_POSTING_STEPS.map((step, index) => [step, index]),
) as Record<JobPostingStep, number>;

/**
 * Whether a step should be shown in the breadcrumb / reachable in the flow,
 * given the channels the user has selected. Pool Setup is the only conditional
 * step today.
 */
export function isStepVisible(
  step: JobPostingStep,
  selectedChannelSlugs: string[],
): boolean {
  if (step === 'pool-setup') return selectedChannelSlugs.includes(POOL);
  if (step === 'result') return false;
  return true;
}

/**
 * Tailwind max-width class for each step's content column, so elements
 * rendered outside the step component (e.g. BackButton) can match its width.
 */
export const STEP_CONTENT_WIDTH: Record<JobPostingStep, string> = {
  'select-channels': 'max-w-[1010px]',
  'job-details': 'max-w-[1010px]',
  'pool-setup': 'max-w-[640px]',
  'campaign-details': 'max-w-screen-sm',
  summary: 'max-w-[784px]',
  result: 'max-w-[586px]',
};

/**
 * Returns the next visible step after `step`, or `null` if there is none.
 */
export function getNextStep(
  step: JobPostingStep,
  selectedChannelSlugs: string[],
): JobPostingStep | null {
  const currentIndex = JOB_POSTING_STEPS.indexOf(step);
  for (let i = currentIndex + 1; i < JOB_POSTING_STEPS.length; i++) {
    const candidate = JOB_POSTING_STEPS[i];
    if (isStepVisible(candidate, selectedChannelSlugs)) return candidate;
  }
  return null;
}

/**
 * Returns the previous visible step before `step`, or `null` if on the first step.
 */
export function getPreviousStep(
  step: JobPostingStep,
  selectedChannelSlugs: string[],
): JobPostingStep | null {
  const currentIndex = JOB_POSTING_STEPS.indexOf(step);
  for (let i = currentIndex - 1; i >= 0; i--) {
    const candidate = JOB_POSTING_STEPS[i];
    if (isStepVisible(candidate, selectedChannelSlugs)) return candidate;
  }
  return null;
}
